import { useSyncExternalStore } from 'react';

export type CookieCategoryId = 'functional' | 'analytics';

export type CookieRegistryEntry = {
  name: string;
  category: CookieCategoryId;
  storage: 'cookie' | 'localStorage' | 'third-party';
  duration: string;
  purpose: string;
};

/** Single source of truth for every cookie/storage item this app sets. */
export const COOKIE_REGISTRY: readonly CookieRegistryEntry[] = [
  {
    name: 'saved_place_ids',
    category: 'functional',
    storage: 'cookie',
    duration: '1 jaar',
    purpose:
      'Onthoudt welke plekken je met het hartje hebt bewaard, zodat je ze later terugvindt.',
  },
  {
    name: 'newsletter_dismissed',
    category: 'functional',
    storage: 'cookie',
    duration: '1 jaar',
    purpose:
      'Onthoudt dat je het nieuwsbriefbericht al hebt weggeklikt, zodat we dat niet steeds opnieuw laten zien.',
  },
  {
    name: 'cf:recent',
    category: 'functional',
    storage: 'localStorage',
    duration: 'tot je je geschiedenis wist',
    purpose:
      'Onthoudt welke plekken je onlangs hebt bekeken, zodat je ze makkelijk terugvindt.',
  },
  {
    name: 'Sentry',
    category: 'analytics',
    storage: 'third-party',
    duration: 'sessie',
    purpose:
      'Helpt ons ontdekken wanneer iets kapot is of traag gaat, zodat we de site kunnen verbeteren.',
  },
];

export const CONSENT_COOKIE_NAME = 'cookie_consent';
export const CONSENT_COOKIE_PURPOSE =
  'Onthoudt welke keuze je hebt gemaakt over cookies, zodat we het niet steeds opnieuw vragen.';

export function entriesForCategory(
  category: CookieCategoryId,
): CookieRegistryEntry[] {
  return COOKIE_REGISTRY.filter((entry) => entry.category === category);
}

export type ConsentValues = {
  functional: boolean;
  analytics: boolean;
};

type ConsentState = ConsentValues & { decidedAt: string };

const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

function isConsentState(value: unknown): value is ConsentState {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as ConsentState).functional === 'boolean' &&
    typeof (value as ConsentState).analytics === 'boolean' &&
    typeof (value as ConsentState).decidedAt === 'string'
  );
}

class CookieConsentStore {
  private listeners = new Set<() => void>();
  private cache: { raw: string | null; value: ConsentState | null } = {
    raw: null,
    value: null,
  };

  read = (): ConsentState | null => {
    if (typeof document === 'undefined') return null;
    const entry = document.cookie
      .split(';')
      .map((part) => part.trim())
      .find((part) => part.startsWith(`${CONSENT_COOKIE_NAME}=`));
    const raw = entry ? entry.slice(CONSENT_COOKIE_NAME.length + 1) : null;
    if (raw === this.cache.raw) return this.cache.value;

    if (raw === null) {
      this.cache = { raw, value: null };
      return this.cache.value;
    }
    try {
      const parsed = JSON.parse(decodeURIComponent(raw));
      this.cache = { raw, value: isConsentState(parsed) ? parsed : null };
    } catch {
      this.cache = { raw, value: null };
    }
    return this.cache.value;
  };

  isDecided = (): boolean => this.read() !== null;

  isAllowed = (category: CookieCategoryId): boolean =>
    this.read()?.[category] ?? false;

  acceptAll = (): void => this.save({ functional: true, analytics: true });

  rejectAll = (): void => this.save({ functional: false, analytics: false });

  save = (values: ConsentValues): void => {
    if (typeof document === 'undefined') return;

    const state: ConsentState = {
      ...values,
      decidedAt: new Date().toISOString(),
    };
    // biome-ignore lint/suspicious/noDocumentCookie: Cookie Store API is not supported in Safari/Firefox yet
    document.cookie = `${CONSENT_COOKIE_NAME}=${encodeURIComponent(JSON.stringify(state))}; max-age=${COOKIE_MAX_AGE}; path=/; SameSite=Lax`;
    this.notify();
  };

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  private notify(): void {
    for (const listener of this.listeners) listener();
  }
}

export const cookieConsent = new CookieConsentStore();

/**
 * Runs `onDenied` once, right now, if `category` is currently not allowed,
 * and again every time it transitions to not-allowed. Lets each
 * cookie/localStorage owner clean up its own data on revocation.
 */
export function onCategoryDenied(
  category: CookieCategoryId,
  onDenied: () => void,
): void {
  if (typeof window === 'undefined') return;
  if (!cookieConsent.isAllowed(category)) onDenied();
  cookieConsent.subscribe(() => {
    if (!cookieConsent.isAllowed(category)) onDenied();
  });
}

export function useCookieConsent() {
  const state = useSyncExternalStore(
    cookieConsent.subscribe,
    cookieConsent.read,
    () => null,
  );

  return {
    decided: state !== null,
    functional: state?.functional ?? false,
    analytics: state?.analytics ?? false,
    acceptAll: cookieConsent.acceptAll,
    rejectAll: cookieConsent.rejectAll,
    save: cookieConsent.save,
  };
}
